/**
 * The standard format indents container bodies by two spaces per level, and Markdown reads four
 * leading spaces as code — so a paragraph two levels deep would parse as a code block. Indentation
 * inside code and opaque tags is content: it neither loses columns nor sets how many others lose.
 */

const OPEN_TAG = /^<([a-zA-Z][\w-]*)(\s[^>]*?)?(\/?)>$/;
// The properties of a block are written on the line of its closing tag: `</tab> <property id="x" />`.
const CLOSE_TAG = /^<\/([a-zA-Z][\w-]*)>(?:[ \t]+<property\s[^>]*\/>)*[ \t]*$/;
const FENCE = /^(```+|~~~+)/;

/** Tags whose body is not Markdown: it is copied through untouched. */
const OPAQUE_TAGS = new Set(["html", "pre"]);

/** How far past the indentation of the surrounding block Markdown starts reading a code block. */
const CODE_INDENT = 4;

type Line = { text: string; indent: number; trimmed: string };

const readLines = (content: string): Line[] =>
	content.split("\n").map((text) => {
		// A file written on Windows keeps `\r`, and the rules below anchor at the end of the line.
		const body = text.endsWith("\r") ? text.slice(0, -1) : text;
		const trimmed = body.trimStart();
		return { text, indent: body.length - trimmed.length, trimmed };
	});

const openTagName = (line: Line) => {
	const match = OPEN_TAG.exec(line.trimmed);
	return match && !match[3] ? match[1] : null;
};

const closeTagName = (line: Line) => CLOSE_TAG.exec(line.trimmed)?.[1] ?? null;

const fenceMarker = (line: Line) => FENCE.exec(line.trimmed)?.[1] ?? null;

type Structure = {
	closedBy: Map<number, number>;
	/** Lines whose indentation is content: code, of one kind or another. */
	verbatim: Set<number>;
};

// A `<note>` shown in a code block opens nothing.
const readStructure = (lines: Line[]): Structure => {
	const closedBy = new Map<number, number>();
	const verbatim = new Set<number>();
	const open: { tag: string; line: number; body: number }[] = [];
	let fence: string = null;
	let indentedCode = false;
	let afterBlank = true;

	const base = () => open.at(-1)?.body ?? 0;

	lines.forEach((line, index) => {
		if (!line.trimmed) {
			// A blank line ends a paragraph but not a code block, and never carries indentation.
			afterBlank = true;
			if (fence || indentedCode) verbatim.add(index);
			return;
		}

		const innermost = open.at(-1);

		if (fence) {
			afterBlank = false;
			const marker = fenceMarker(line);
			if (marker?.[0] !== fence[0] || marker.length < fence.length) {
				verbatim.add(index);
				return;
			}
			// The marker that closes the fence is structure, and stands where the container put it.
			fence = null;
			if (innermost) innermost.body = Math.min(innermost.body, line.indent);
			return;
		}

		if (indentedCode) {
			if (line.indent - base() >= CODE_INDENT) {
				verbatim.add(index);
				afterBlank = false;
				return;
			}
			indentedCode = false;
		}

		if (afterBlank && line.indent - base() >= CODE_INDENT) {
			indentedCode = true;
			verbatim.add(index);
			afterBlank = false;
			return;
		}

		afterBlank = false;
		if (innermost) innermost.body = Math.min(innermost.body, line.indent);

		const marker = fenceMarker(line);
		if (marker) {
			fence = marker;
			return;
		}

		const opened = openTagName(line);
		if (opened) {
			open.push({ tag: opened, line: index, body: Number.POSITIVE_INFINITY });
			return;
		}

		const closed = closeTagName(line);
		if (!closed) return;

		let depth = open.length - 1;
		while (depth >= 0 && open[depth].tag !== closed) depth--;
		if (depth === -1) return;

		closedBy.set(open[depth].line, index);
		open.length = depth;
	});

	return { closedBy, verbatim };
};

const unindent = (line: Line, indent: number) => line.text.slice(Math.min(indent, line.indent));

const dedent = (line: Line, indent: number) => (line.trimmed ? unindent(line, indent) : "");

// preParse runs on every article, most without a container: one scan for a tag line
// is cheaper than reading the structure.
const TAG_LINE = /^[ \t]*<\/?[a-zA-Z][\w-]*(\s[^>]*?)?\/?>[ \t]*\r?$/m;

const dedentContainers = (content: string): string => {
	if (!TAG_LINE.test(content)) return content;

	const lines = readLines(content);
	const { closedBy, verbatim } = readStructure(lines);
	if (closedBy.size === 0) return content;

	const addedIndent = (openLine: number, closeLine: number) => {
		let smallest = Number.POSITIVE_INFINITY;
		for (let index = openLine + 1; index < closeLine; index++) {
			if (!lines[index].trimmed || verbatim.has(index)) continue;
			smallest = Math.min(smallest, lines[index].indent);
		}
		if (!Number.isFinite(smallest)) return 0;
		return Math.max(smallest - lines[openLine].indent, 0);
	};

	const result: string[] = [];

	const writeRange = (from: number, to: number, indent: number) => {
		for (let index = from; index < to; index++) {
			const line = lines[index];
			result.push(dedent(line, indent));

			const closeLine = closedBy.get(index);
			if (closeLine === undefined || closeLine >= to) continue;

			if (OPAQUE_TAGS.has(openTagName(line))) {
				// Everything past the columns the outer containers added belongs to the author.
				for (let inner = index + 1; inner < closeLine; inner++) result.push(unindent(lines[inner], indent));
			} else {
				writeRange(index + 1, closeLine, indent + addedIndent(index, closeLine));
			}

			result.push(dedent(lines[closeLine], indent));
			index = closeLine;
		}
	};

	writeRange(0, lines.length, 0);
	return result.join("\n");
};

export default dedentContainers;
